import { CheckmarkCircle02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import {
  useAutoFavoriteSession,
  useGetEvent,
  useGetSession,
  useGetTicketTypesByEventId,
  useUserEventTicket,
} from '@sunrise-ui/api-client';
import {
  Colors,
  TextReg,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { IconButton as PaperIconButton } from 'react-native-paper';
import { useTicketPurchase } from '../../hooks/useTicketPurchase';

type Props = {
  eventId?: string;
  sessionId?: string;
  pageCommunityId?: string; // ID of the current community page to check for "Included" sessions
  isSingleSessionEvent?: boolean; // Whether this session belongs to a single session event
};

export function SessionTicketCTA({
  eventId,
  sessionId,
  pageCommunityId,
  isSingleSessionEvent,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const [hideComponent, setHideComponent] = useState(false);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { data: session, isLoading: isLoadingSession } =
    useGetSession(sessionId);

  const { data: event, isLoading: isLoadingEvent } = useGetEvent(eventId);

  const {
    data: notOnSaleTicketTypes,
    isLoading: isLoadingNotOnSaleTicketTypes,
  } = useGetTicketTypesByEventId({
    eventId,
    isOnSale: false,
  });

  const { data: onSaleTicketTypes, isLoading: isLoadingOnSaleTicketTypes } =
    useGetTicketTypesByEventId({
      eventId,
      isOnSale: true,
    });

  const { userTicket, isLoadingTickets } = useUserEventTicket(
    eventId,
    event?.community_id,
  );

  const { autoFavorite } = useAutoFavoriteSession(
    sessionId,
    eventId,
    event?.community_id,
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const isExternallyTicketed = Boolean(event?.ticketing_platform_url);

  // Show success animation after successful purchase or when user already has ticket
  const showSuccessAnimation = () => {
    setShowSuccessState(true);
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccessState(false);
      hideTimeoutRef.current = setTimeout(() => {
        setHideComponent(true);
      }, 150);
    }, 2000);
  };

  // Handle ticket purchase
  const { executePurchase, isPurchasing, isLoadingPurchase } =
    useTicketPurchase({
      eventId,
      onSuccess: async () => {
        await autoFavorite();
        showSuccessAnimation();
      },
    });

  const isLoadingTicketTypes =
    isLoadingNotOnSaleTicketTypes || isLoadingOnSaleTicketTypes;
  const hasAnyTicketTypes = Boolean(
    notOnSaleTicketTypes?.length || onSaleTicketTypes?.length,
  );

  const isLoading =
    isLoadingEvent ||
    isLoadingTicketTypes ||
    isLoadingSession ||
    isLoadingPurchase ||
    isLoadingTickets;

  const isSessionCompleted = useMemo(() => {
    if (!session?.start_date || !session?.end_date) return false;

    const now = DateTime.now();
    const endTime = DateTime.fromISO(session.end_date);

    return now > endTime;
  }, [session]);

  // Hide component after successful ticket purchase
  if (hideComponent || isSessionCompleted) {
    return null;
  }

  // Show success state with orange checkmark
  if (showSuccessState) {
    return (
      <View style={styles.circularButton}>
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={20}
          color={Colors.orange0}
        />
      </View>
    );
  }

  // Show loading state while fetching data or getting ticket
  if (isLoading || isPurchasing) {
    return (
      <PaperIconButton
        style={[styles.iconButton, styles.loadingButton]}
        disabled={true}
        icon={() => <ActivityIndicator size="small" color={Colors.tint} />}
        iconColor={Colors.text}
        size={20}
      />
    );
  }

  // If this session's event belongs to the current page community, show "Included" instead
  // BUT NOT for single session events on the parent community page
  if (
    pageCommunityId &&
    event?.community_id === pageCommunityId &&
    !isSingleSessionEvent
  ) {
    return (
      <View style={styles.includedContainer}>
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={16}
          color={Colors.text}
          style={styles.includedIcon}
        />
        <TextReg style={styles.includedText}>Included</TextReg>
      </View>
    );
  }

  // Don't show anything if user has ticket to THIS event
  if (userTicket) {
    return null;
  }

  // Don't show if no ticket types exist at all (and not externally ticketed)
  if (!hasAnyTicketTypes && !isExternallyTicketed) {
    return null;
  }

  return (
    <PaperIconButton
      onPress={() => {
        void executePurchase();
      }}
      style={[styles.iconButton]}
      loading={isPurchasing}
      disabled={isPurchasing}
      icon={() => <TextReg style={styles.emojiText}>🎟</TextReg>}
      iconColor={Colors.text}
      size={20}
    />
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    includedContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8, // Increased gap between icon and text
    },
    includedText: {
      fontSize: isMobile ? 12 : 14,
      color: Colors.text,
      opacity: 0.8, // Match time and location text styling
    },
    includedIcon: {
      opacity: 0.8, // Match time and location text styling
    },
    iconButton: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: Colors.opacity20, // Use consistent Colors constant
      margin: 0, // Remove default margin from PaperIconButton
    },
    emojiText: {
      fontSize: 20, // Even bigger emoji size
    },
    loadingButtonLabel: {
      marginLeft: 16,
      marginRight: 16,
    },
    loadingButton: {
      backgroundColor: 'rgba(255, 255, 255, 0.25)', // Slightly more visible during loading
    },
    circularButton: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: 'rgba(255, 255, 255, 0.15)', // Much more transparent
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 1,
      elevation: 1, // Lighter shadow for more subtle appearance
    },
  });
